import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getMyDashboard, saveMyGoal } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import MyCommission from '../components/MyCommission';

/* My Dashboard — the home page for staff logins (reps, canvassers, telemarketers, confirmers).
   Only their own numbers, worked out on the server from the job records and appointments.
   A forest-green money band on top (sales this month, goal and pace), then three tabs:
   Today (commission, appointments, focus), My Numbers (scorecard vs team average, trend) and My Deals. */

const CSS = `
.rd{--bg:#f2f4ee;--surface:#fff;--ink:#16241c;--muted:#566659;--line:#d7dfd4;--soft:#e8ede4;--band:#12372a;--band2:#1b4a39;--band-ink:#f5f2e6;--band-muted:#b9c9bc;
  --gold:#d4ad36;--gold-ink:#7f620a;--on-gold:#1b1604;--good:#17744a;--good-bg:#dcf0e4;--warn:#955700;--warn-bg:#f8ead1;--bad:#ab3026;--bad-bg:#f8e0dd;
  --display:"Barlow Condensed","Arial Narrow",Arial,sans-serif;--body:"Source Sans 3","Segoe UI",system-ui,sans-serif;
  background:var(--bg);color:var(--ink);font:400 16px/1.45 var(--body);font-variant-numeric:tabular-nums;min-height:100vh}
.rd *{box-sizing:border-box}
.rd h2,.rd h3{font-family:var(--display);margin:0}
.rd h2{font-size:1.45rem;font-weight:700;letter-spacing:.01em}
.rd-wrap{max-width:1080px;margin:0 auto;padding:0 20px}
.rd-band{background:var(--band);color:var(--band-ink);padding:18px 0 22px}
.rd-top{display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap;margin-bottom:14px}
.rd-who{display:flex;align-items:center;gap:12px;font-family:var(--display);font-size:1.25rem;font-weight:600;letter-spacing:.02em}
.rd-who img,.rd-who .ini{width:44px;height:44px;border-radius:50%;object-fit:cover;border:2px solid var(--gold);background:var(--band2);display:flex;align-items:center;justify-content:center;font-weight:700;color:var(--gold)}
.rd-who span{color:var(--band-muted);font-weight:500}
.rd-eyebrow{font-size:.78rem;letter-spacing:.07em;text-transform:uppercase;color:var(--band-muted);font-weight:600}
.rd-month{display:flex;align-items:center;gap:4px}
.rd-month button{border:1px solid var(--band2);background:transparent;color:var(--band-ink);border-radius:8px;min-width:36px;height:34px;font:inherit;font-weight:700;cursor:pointer}
.rd-month button:hover{background:var(--band2)}
.rd-month button:disabled{opacity:.35;cursor:default}
.rd-link{border:0;background:none;padding:4px 6px;color:var(--band-muted);text-decoration:underline;font:inherit;font-size:.92rem;cursor:pointer}
.rd-money{display:grid;grid-template-columns:minmax(0,1.3fr) minmax(0,2fr);gap:22px;align-items:end}
.rd-big{font:700 clamp(3rem,10vw,4.8rem)/.95 var(--display);color:var(--gold)}
.rd-bigsub{color:var(--band-muted);font-size:.95rem;margin-top:6px}
.rd-minis{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px}
.rd-mini{min-width:0;border-left:1px solid var(--band2);padding-left:12px}
.rd-mini b{display:block;font:600 1.65rem/1.1 var(--display);margin-top:2px;overflow-wrap:anywhere}
.rd-mini small{display:block;color:var(--band-muted);font-size:.8rem}
.rd-mini.risk b{color:#f6b3a8}
.rd-roles{display:flex;gap:6px;flex-wrap:wrap;margin:-4px 0 14px}
.rd-roles button{border:1px solid var(--band2);background:transparent;color:var(--band-muted);border-radius:999px;padding:4px 12px;font:600 .9rem var(--body);cursor:pointer}
.rd-roles button.on{background:var(--gold);border-color:var(--gold);color:var(--on-gold)}
.rd-pace{margin-top:18px;border-top:1px solid var(--band2);padding-top:14px}
.rd-pace-head{display:flex;justify-content:space-between;gap:10px;flex-wrap:wrap;align-items:baseline}
.rd-track{position:relative;height:14px;background:var(--band2);border-radius:7px;margin:10px 0 26px}
.rd-fill{position:absolute;inset:0 auto 0 0;background:var(--gold);border-radius:7px;min-width:2px;transition:width .3s}
.rd-mark{position:absolute;top:-4px;bottom:-4px;width:2px;background:var(--band-ink)}
.rd-mark span{position:absolute;top:24px;transform:translateX(-50%);white-space:nowrap;font-size:.76rem;color:var(--band-muted)}
.rd-mark.lo span{transform:none;left:0}.rd-mark.hi span{transform:translateX(-100%)}
.rd-pill{display:inline-block;padding:2px 9px;border-radius:999px;font-size:.84rem;font-weight:700;white-space:nowrap}
.rd-pill.good{background:var(--good-bg);color:var(--good)}.rd-pill.warn{background:var(--warn-bg);color:var(--warn)}.rd-pill.bad{background:var(--bad-bg);color:var(--bad)}.rd-pill.flat{background:var(--soft);color:var(--muted)}
.rd-btn{border:1px solid var(--line);background:var(--surface);border-radius:8px;padding:8px 12px;font:600 15px var(--body);min-height:40px;cursor:pointer;color:var(--ink);text-decoration:none;display:inline-flex;align-items:center;gap:6px}
.rd-btn:hover{background:var(--soft)}
.rd-btn.gold{background:var(--gold);border-color:var(--gold);color:var(--on-gold)}
.rd-btn.small{min-height:34px;padding:4px 10px;font-size:.9rem}
.rd-tabs{position:sticky;top:0;z-index:5;background:var(--surface);border-bottom:1px solid var(--line)}
.rd-tabs .rd-wrap{display:grid;grid-template-columns:repeat(3,minmax(0,1fr))}
.rd-tab{border:0;background:none;padding:12px 4px;font:600 1.1rem var(--display);letter-spacing:.03em;color:var(--muted);border-bottom:3px solid transparent;cursor:pointer}
.rd-tab.on{color:var(--ink);border-bottom-color:var(--gold)}
.rd-main{padding:18px 0 40px}
.rd-grid{display:grid;gap:18px;grid-template-columns:minmax(0,3fr) minmax(0,2fr);align-items:start}
.rd-stack{display:grid;gap:18px;min-width:0}
.rd-sec{background:var(--surface);border:1px solid var(--line);border-radius:10px;padding:14px 16px;min-width:0}
.rd-sechead{display:flex;justify-content:space-between;align-items:center;gap:10px;margin-bottom:8px;flex-wrap:wrap}
.rd-note{color:var(--muted);font-size:.93rem}
.rd-row{display:flex;gap:10px;justify-content:space-between;align-items:flex-start;padding:11px 0;border-top:1px solid var(--line);flex-wrap:wrap}
.rd-row:first-of-type{border-top:0}
.rd-row .main{flex:1 1 220px;min-width:0;overflow-wrap:anywhere}
.rd-row .title{font-weight:700}
.rd-row .acts{display:flex;gap:6px;flex-wrap:wrap}
.rd-time{font:600 1.1rem var(--display);margin-right:8px}
.rd-sub{display:block;font-size:.86rem;color:var(--muted);font-weight:400}
.rd-empty{padding:14px 0;color:var(--muted)}
.rd table{width:100%;border-collapse:collapse}
.rd th{font-size:.78rem;letter-spacing:.05em;text-transform:uppercase;color:var(--muted);text-align:right;padding:6px 8px;font-weight:600}
.rd td{padding:11px 8px;border-top:1px solid var(--line);text-align:right;white-space:nowrap}
.rd th:first-child,.rd td:first-child{text-align:left;padding-left:0;white-space:normal}
.rd td.val{font:700 1.3rem var(--display)}
.rd-callout{background:var(--band);color:var(--band-ink);border-radius:10px;padding:14px 16px}
.rd-callout .rd-eyebrow{color:var(--gold)}
.rd-callout p{margin:6px 0 0}
.rd-rank{display:flex;align-items:baseline;gap:8px}
.rd-rank b{font:700 3rem/1 var(--display);color:var(--gold-ink)}
.rd-bars{display:flex;align-items:flex-end;gap:10px;height:150px;padding-top:8px}
.rd-bars div{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:flex-end;height:100%;gap:4px;min-width:0}
.rd-bars i{display:block;width:100%;max-width:56px;background:var(--band2);border-radius:5px 5px 0 0;min-height:2px}
.rd-bars div.cur i{background:var(--gold)}
.rd-bars small{font-size:.78rem;color:var(--muted)}
.rd-bars em{font-style:normal;font:600 .9rem var(--display)}
.rd-whatif output{font:700 2.1rem/1.1 var(--display);color:var(--gold-ink);display:block}
.rd input[type=range]{width:100%;accent-color:var(--gold);margin:10px 0}
.rd-links{display:flex;flex-wrap:wrap;gap:8px}
.rd-sheet-bg{position:fixed;inset:0;background:rgba(5,15,10,.6);z-index:2000;display:flex;align-items:center;justify-content:center;padding:16px}
.rd-sheet{background:var(--surface);border-radius:12px;padding:20px;width:min(440px,100%)}
.rd-sheet output{font:700 2.4rem/1.1 var(--display);color:var(--gold-ink);display:block;margin-top:6px}
.rd-sheet .acts{display:flex;gap:8px;justify-content:flex-end;margin-top:14px}
.rd-strip{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:8px;margin:4px 0 14px}
.rd-per{border:1px solid var(--band2);background:rgba(255,255,255,.03);border-radius:10px;padding:10px 12px;text-align:left;color:var(--band-ink);cursor:pointer;font:inherit;min-width:0}
.rd-per:hover{background:var(--band2)}
.rd-per b{display:block;font:700 1.7rem/1.1 var(--display);margin-top:2px;overflow-wrap:anywhere}
.rd-per small{display:block;color:var(--band-muted);font-size:.8rem}
.rd-per.on{border-color:var(--gold);background:var(--band2);box-shadow:inset 0 -3px 0 var(--gold)}
.rd-per.on b{color:var(--gold)}
.rd-tiles{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px}
.rd-tile{background:rgba(0,0,0,.14);border-radius:10px;padding:11px 13px;min-width:0}
.rd-tile b{display:block;font:700 2rem/1.05 var(--display);margin-top:3px;overflow-wrap:anywhere}
.rd-tile small{display:block;color:var(--band-muted);font-size:.82rem;margin-top:2px}
.rd-tile.gold b{color:var(--gold)}
.rd-tile.risk b{color:#f6b3a8}
.rd-comm-nums{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;margin:4px 0 14px}
.rd-comm-nums div{background:var(--soft);border-radius:10px;padding:10px 12px;min-width:0}
.rd-comm-nums b{display:block;font:700 2rem/1.05 var(--display);margin-top:2px;overflow-wrap:anywhere}
.rd-comm-nums b.gold{color:var(--gold-ink)}
.rd-comm-nums small{display:block;color:var(--muted);font-size:.84rem}
.rd-comm-goal{border-top:1px solid var(--line);padding-top:12px}
.rd-ctrack{position:relative;height:14px;background:var(--soft);border-radius:7px;margin:10px 0 6px}
.rd-cfill{position:absolute;inset:0 auto 0 0;background:var(--gold);border-radius:7px;min-width:2px;transition:width .3s}
.rd-cmark{position:absolute;top:-4px;bottom:-4px;width:2px;background:var(--ink)}
.rd-comm-need{margin:6px 0}
.rd-comm-need b{font-weight:700}
.rd-seg{display:inline-flex;border:1px solid var(--line);border-radius:8px;overflow:hidden}
.rd-seg button{border:0;background:var(--surface);padding:6px 12px;font:600 .92rem var(--body);cursor:pointer;color:var(--muted);min-height:34px}
.rd-seg button.on{background:var(--band);color:var(--band-ink)}
.rd-comm input[type=range]{height:auto;border:0;padding:0;background:none;box-shadow:none}
@media(max-width:620px){.rd-comm-nums{gap:6px}.rd-comm-nums div{padding:8px}.rd-comm-nums b{font-size:1.35rem}.rd-comm-nums small{font-size:.74rem}.rd-comm-nums .rd-eyebrow{font-size:.66rem}}
.rd-under{color:var(--band-muted);font-size:.88rem;margin-top:10px}
@media(max-width:980px){.rd-strip{grid-template-columns:repeat(3,minmax(0,1fr))}.rd-tiles{grid-template-columns:repeat(2,minmax(0,1fr))}}
@media(max-width:780px){.rd-strip{grid-template-columns:repeat(2,minmax(0,1fr))}.rd-money{grid-template-columns:minmax(0,1fr);gap:14px}.rd-minis{grid-template-columns:repeat(2,minmax(0,1fr))}.rd-grid{grid-template-columns:minmax(0,1fr)}.rd-wrap{padding:0 14px}}
`;

const usd = n => (n == null ? '—' : (n < 0 ? '-' : '') + '$' + Math.round(Math.abs(n)).toLocaleString('en-US'));
const pctT = n => (n == null ? '—' : n + '%');
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const monthName = m => MONTHS[+m.slice(5, 7) - 1];
const niceDate = s => (s ? new Date(s + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }) : '');
const time12 = t => { const m = /^(\d{1,2}):(\d{2})/.exec(t || ''); if (!m) return t || ''; const h = +m[1]; return `${h % 12 || 12}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`; };
const mapLink = l => `https://maps.google.com/?q=${encodeURIComponent([l.address, l.city, l.zip].filter(Boolean).join(', '))}`;
const tel = p => `tel:${String(p || '').replace(/[^\d+]/g, '')}`;
const ROLE_KIND = { 'Sales Rep': 'rep', Canvasser: 'set', Telemarketer: 'set', Confirmer: 'conf' };

// Stage of a job from its statuses (same steps as My Jobs)
function stageOf(d) {
  const p = d.prod_status.toLowerCase(), s = d.sales_status.toLowerCase();
  if (d.cancelled) return ['bad', 'Cancelled'];
  if (p.includes('funded') || p.includes('complete') || p.includes('closed') || s === 'closed') return ['good', 'Complete'];
  if (p.includes('funding') || p.includes('clear to collect')) return ['good', 'Funding'];
  if (p.includes('cert') || p.includes('permit')) return ['flat', 'Inspection / cert'];
  if (p) return ['warn', 'In production'];
  return ['flat', 'Sold'];
}

export default function MyDashboard() {
  const { username, isAdmin } = useAuth();
  const [params] = useSearchParams();
  const preview = isAdmin ? params.get('staff') : null;   // admins can preview someone's dashboard
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('today');
  const [kind, setKind] = useState(null);
  const [per, setPer] = useState(null);
  const [goalOpen, setGoalOpen] = useState(false);

  useEffect(() => {
    if (document.getElementById('rd-fonts')) return;
    const l = document.createElement('link'); l.id = 'rd-fonts'; l.rel = 'stylesheet';
    l.href = 'https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@500;600;700&family=Source+Sans+3:wght@400;600;700&display=swap';
    document.head.appendChild(l);
  }, []);
  const load = useCallback(() => {
    getMyDashboard(undefined, preview || undefined).then(x => { setD(x); setErr(''); })
      .catch(e => setErr(e.response?.data?.error || 'Could not load your numbers'));
  }, [preview]);
  useEffect(() => { load(); }, [load]);

  // Which job's numbers to lead with: the roles they have, then any role they have appointments in
  const kinds = useMemo(() => {
    if (!d?.linked) return [];
    const k = [...new Set(d.roles.map(r => ROLE_KIND[r]).filter(Boolean))];
    ['rep', 'set', 'conf'].forEach(x => { if (!k.includes(x) && d.periods.all.appts[x]) k.push(x); });
    return k.length ? k : ['rep'];
  }, [d]);
  const K = kind && kinds.includes(kind) ? kind : kinds[0];
  // Open on the shortest period that actually has numbers in it
  const P = useMemo(() => {
    if (!d?.linked) return 'month';
    if (per) return per;
    const has = p => d.periods[p].sales.jobs > 0 || (d.periods[p].appts[K]?.set || 0) >= 5;
    return ['month', 'year', 'last12', 'all'].find(has) || 'month';
  }, [d, per, K]);

  if (err) return <div className="rd"><style>{CSS}</style><div className="rd-wrap" style={{ padding: 30 }}>{err}</div></div>;
  if (!d) return <div className="rd"><style>{CSS}</style><div className="rd-band"><div className="rd-wrap"><div className="rd-eyebrow">CPR Home Solutions</div><p>Loading your numbers…</p></div></div></div>;
  if (!d.linked) return (
    <div className="rd"><style>{CSS}</style>
      <div className="rd-band"><div className="rd-wrap" style={{ maxWidth: 640, padding: '26px 20px' }}>
        <div className="rd-eyebrow">CPR Home Solutions</div>
        <h1 style={{ font: '700 2.4rem/1.05 var(--display)', color: 'var(--gold)', margin: '6px 0 10px' }}>Your dashboard isn't linked yet</h1>
        <p>Your login ({username}) isn't connected to anyone on the Staff list, so there are no numbers to show. Ask the office to link your login to your name on the Users page.</p>
      </div></div>
    </div>
  );

  const cur = d.periods[P], A = cur.appts[K], T = cur.team[K], s = cur.sales;
  const mo = d.periods.month;
  const elapsed = d.day / d.dim;
  const expected = d.goal * elapsed, diff = mo.sales.revenue - expected;
  const done = d.goal ? Math.min(mo.sales.revenue / d.goal, 1) : 0;
  const avgForGoal = mo.sales.avgTicket || d.periods.last12.sales.avgTicket || d.periods.all.sales.avgTicket;
  const shareAll = d.periods.last12.sales.full ? d.periods.last12.sales.revenue / d.periods.last12.sales.full : 1;
  const salesLeft = d.goal && avgForGoal ? Math.ceil(Math.max(d.goal - mo.sales.revenue, 0) / (avgForGoal * shareAll)) : null;
  const A12 = d.periods.last12.appts.rep;
  const sitRate = A12 && A12.sat ? A12.sold / A12.sat : null;
  const apptsLeft = salesLeft && sitRate ? Math.ceil(salesLeft / sitRate) : null;
  let need = '';
  if (d.goal && mo.sales.revenue >= d.goal) need = 'Goal hit. Everything from here is extra.';
  else if (d.goal && salesLeft != null) need = `${salesLeft} more ${salesLeft === 1 ? 'sale' : 'sales'} at your ${usd(avgForGoal)} average ticket hits your goal${apptsLeft && K === 'rep' ? `. At your sit-down rate, that's about ${apptsLeft} sit-downs.` : '.'}`;
  else if (d.goal) need = `You need ${usd(d.goal - mo.sales.revenue)} more in sales this month to hit your goal.`;
  const mkc = elapsed < 0.15 ? 'lo' : elapsed > 0.85 ? 'hi' : '';

  // The key numbers for the chosen period, by job
  const pctOf = (a, b) => (b ? Math.round(a / b * 100) : null);
  const tiles = K === 'rep' ? [
    ['Sales', usd(s.revenue), `${s.jobs} ${s.jobs === 1 ? 'job' : 'jobs'} sold${s.full > s.revenue ? ` · share of ${usd(s.full)}` : ''}`, 'gold'],
    ['Average ticket', usd(s.avgTicket), 'full job value'],
    ['Appointments', A ? A.set : 0, A ? `${A.trips} trips · ${A.sat} sit-downs` : 'none in this period'],
    ['Sit-down → sale', pctT(A?.sitSale), A ? `${A.sold} sold of ${A.sat} sit-downs` : '—'],
    ['Close on demos', pctT(A?.close), A ? `${A.sold} of ${A.sold + A.demo} full demos` : '—'],
    ['Per sit-down', usd(A?.perSit), 'sales ÷ sit-downs'],
    ['Not your fault', A ? A.wasted : 0, A && A.trips ? `${pctOf(A.wasted, A.trips)}% of trips: no show / no opp` : '—'],
    ['Cancelled', s.cancelled.jobs, s.cancelled.jobs ? `${usd(s.cancelled.revenue)} · ${s.cancelRate}% of sales` : 'none', 'risk'],
  ] : K === 'set' ? [
    ['Appointments set', A ? A.set : 0, A ? `${A.trips} trips for the reps` : 'none in this period', 'gold'],
    ['Owners home', pctT(A?.ownersHome), A ? `rep sat down on ${A.sat} of ${A.trips} trips` : '—'],
    ['Sold from mine', A ? A.sold : 0, A && A.set ? `${pctOf(A.sold, A.set)}% of what you set` : '—'],
    ['Sales from mine', usd(A?.revenue), 'appointments that closed'],
    ['Wasted trips', A ? A.wasted : 0, A && A.trips ? `${pctOf(A.wasted, A.trips)}% · no show / no opp / NRA` : '—', 'risk'],
    ['Cancelled', A ? A.canc : 0, 'before the rep went'],
    ['My credited sales', usd(s.revenue), `${s.jobs} ${s.jobs === 1 ? 'job' : 'jobs'} on the records`],
    ['Average ticket', usd(s.avgTicket), 'on jobs you set'],
  ] : [
    ['Confirmed', A ? A.set : 0, A ? `${A.trips} trips for the reps` : 'none in this period', 'gold'],
    ['Owners home', pctT(A?.ownersHome), A ? `on ${A.trips} trips after confirming` : '—'],
    ['Sold', A ? A.sold : 0, A && A.set ? `${pctOf(A.sold, A.set)}% of what you confirmed` : '—'],
    ['Sales from mine', usd(A?.revenue), 'appointments that closed'],
    ['No-shows', A ? A.wasted : 0, A && A.trips ? `${pctOf(A.wasted, A.trips)}% of trips` : '—', 'risk'],
    ['Cancelled', A ? A.canc : 0, 'caught before the trip'],
    ['My credited sales', usd(s.revenue), `${s.jobs} ${s.jobs === 1 ? 'job' : 'jobs'} on the records`],
    ['Average ticket', usd(s.avgTicket), 'on jobs you confirmed'],
  ];
  const strip = ['month', 'prev', 'year', 'last12', 'all'].map(k => {
    const p = d.periods[k], a = p.appts[K];
    const main = K === 'rep' ? usd(p.sales.revenue) : String(a ? a.set : 0);
    const sub = K === 'rep' ? `${p.sales.jobs} ${p.sales.jobs === 1 ? 'job' : 'jobs'}${a ? ` · ${a.sat} sit-downs` : ''}` : `${a ? a.sold : 0} sold${a && a.ownersHome != null ? ` · ${a.ownersHome}% home` : ''}`;
    return { k, label: p.label, main, sub };
  });

  // Today's focus: the biggest gap against the team average over the last 12 months (enough to be fair)
  const L = d.periods.last12, LA = L.appts[K], LT = L.team[K];
  const gaps = [];
  const gap = (mine, team, msg) => { if (mine != null && team) { const g = (team - mine) / team; if (g > 0.08) gaps.push([g, msg]); } };
  if (LA && K === 'rep') {
    gap(LA.sitSale, LT?.sitSale, 'You sell fewer of your sit-downs than the team. Run closing rounds in the Objection Room today.');
    gap(LA.close, LT?.close, 'Your full demos close below the team. Rehearse your close with the presentation scripts before tonight.');
  }
  if (LA && K !== 'rep') gap(LA.ownersHome, LT?.ownersHome, K === 'set' ? 'Owners are home less often on your appointments than the team. Confirm both owners will be there before you book.' : 'More of your confirmed appointments end up with no one home. Call twice and confirm both owners.');
  gaps.sort((a, b) => b[0] - a[0]);
  const focus = gaps[0]?.[1] || (LA ? 'Over the last 12 months you\'re at or above the team on your key numbers. Keep it going.' : null);

  const rows = K === 'rep' ? [
    ['Sit-down → sale', 'Sold ÷ times you sat with the owners', A?.sitSale, T?.sitSale, pctT, true],
    ['Close rate on demos', 'Sold ÷ full demos run', A?.close, T?.close, pctT, true],
    ['Revenue per sit-down', 'Sales ÷ sit-downs', A?.perSit, T?.perSit, usd, true],
    ['Owners home', 'Trips where you sat down (when not, it isn\'t your fault)', A?.ownersHome, T?.ownersHome, pctT, true],
  ] : [
    ['Owners home', 'Trips where the rep sat down ÷ all trips', A?.ownersHome, T?.ownersHome, pctT, true],
    ['Sold rate', 'Sold ÷ appointments', A && A.set ? pctOf(A.sold, A.set) : null, T && T.set ? pctOf(T.sold, T.set) : null, pctT, true],
    ['Wasted trips', 'No show, no opp or NRA ÷ trips', A && A.trips ? pctOf(A.wasted, A.trips) : null, T && T.trips ? pctOf(T.wasted, T.trips) : null, pctT, false],
  ];
  const tag = (mine, team, high) => {
    if (mine == null || team == null) return null;
    const dd = mine - team; if (Math.abs(dd) <= Math.abs(team) * 0.02) return <span className="rd-pill flat">Even</span>;
    const better = high ? dd > 0 : dd < 0;
    return <span className={'rd-pill ' + (better ? 'good' : 'bad')}>{better ? 'Better than team' : 'Below team'}</span>;
  };
  const maxTrend = Math.max(1, ...d.trend.map(t => t.revenue));
  const firstName = d.name.split(/\s+/)[0];
  const sinceTxt = d.since ? `with CPR since ${monthName(d.since)} ${d.since.slice(0, 4)}` : '';
  const rank = cur.rank || d.periods.year.rank || d.periods.last12.rank;

  return (
    <div className="rd">
      <style>{CSS}</style>
      <header className="rd-band"><div className="rd-wrap">
        <div className="rd-top">
          <div className="rd-who">{d.photo ? <img src={d.photo} alt="" /> : <span className="ini">{firstName[0]}</span>}
            <div>{d.name}{preview ? ' (preview)' : ''} <span>· {monthName(d.today)} {Number(d.today.slice(8))}{sinceTxt ? ` · ${sinceTxt}` : ''}</span></div></div>
          {!preview && <button type="button" className="rd-link" onClick={() => setGoalOpen(true)}>{d.goal ? 'Edit monthly goal' : 'Set a monthly goal'}</button>}
        </div>
        {kinds.length > 1 && <div className="rd-roles">{kinds.map(k => <button key={k} type="button" className={k === K ? 'on' : ''} onClick={() => setKind(k)}>{{ rep: 'As sales rep', set: 'As setter', conf: 'As confirmer' }[k]}</button>)}</div>}

        <div className="rd-strip" role="tablist" aria-label="Period">
          {strip.map(x => (
            <button key={x.k} type="button" role="tab" aria-selected={x.k === P} className={'rd-per' + (x.k === P ? ' on' : '')} onClick={() => setPer(x.k)}>
              <span className="rd-eyebrow">{x.label}</span><b>{x.main}</b><small>{x.sub}</small>
            </button>))}
        </div>

        <div className="rd-tiles">
          {tiles.map(([k, v, sub, cls]) => <div key={k} className={'rd-tile' + (cls ? ' ' + cls : '')}><span className="rd-eyebrow">{k}</span><b>{v}</b><small>{sub}</small></div>)}
        </div>
        <div className="rd-under">{cur.label}{cur.from > '0000-01-01' ? ` · ${niceDate(cur.from)} – ${niceDate(cur.to)}` : ''}{rank ? ` · #${rank.rank} of ${rank.of} ${rank.role.toLowerCase()}s in sales${rank === cur.rank ? '' : ' this year'}` : ''}</div>

        {d.goal > 0 && (
          <div className="rd-pace">
            <div className="rd-pace-head"><span><span className="rd-eyebrow">This month's goal</span> <strong>{usd(mo.sales.revenue)} of {usd(d.goal)}</strong></span>
              {diff >= 0 ? <span className="rd-pill good">Ahead {usd(diff)}</span> : <span className={'rd-pill ' + (diff > -d.goal * 0.1 ? 'warn' : 'bad')}>Behind {usd(-diff)}</span>}</div>
            <div className="rd-track" role="img" aria-label={`${usd(mo.sales.revenue)} of ${usd(d.goal)} goal; pace today is ${usd(expected)}`}>
              <div className="rd-fill" style={{ width: `${done * 100}%` }} /><div className={'rd-mark ' + mkc} style={{ left: `${elapsed * 100}%` }}><span>Pace today {usd(expected)}</span></div></div>
            <div>{need}</div>
          </div>
        )}
        {!d.goal && !preview && <div className="rd-pace"><button type="button" className="rd-btn gold" onClick={() => setGoalOpen(true)}>Set my monthly goal</button> <span style={{ color: 'var(--band-muted)', marginLeft: 8 }}>See every day if you're ahead or behind pace.</span></div>}
      </div></header>

      <nav className="rd-tabs" aria-label="Dashboard sections"><div className="rd-wrap">
        {[['today', 'Today'], ['numbers', 'My Numbers'], ['deals', 'My Deals']].map(([k, l]) => <button key={k} type="button" className={'rd-tab' + (tab === k ? ' on' : '')} onClick={() => setTab(k)}>{l}</button>)}
      </div></nav>

      <main className="rd-wrap rd-main">
        {tab === 'today' && (<>
          {d.commission && <div style={{ marginBottom: 18 }}><MyCommission c={d.commission} today={d.today} dim={d.dim} preview={!!preview} onGoalSaved={v => setD(x => ({ ...x, commission: { ...x.commission, goal: v } }))} /></div>}
          <div className="rd-grid"><div className="rd-stack">
            {focus && <section className="rd-callout"><div className="rd-eyebrow">Today's focus</div><p>{focus}</p></section>}
            <section className="rd-sec"><div className="rd-sechead"><h2>My appointments</h2><span className="rd-note">next 14 days</span></div>
              {d.upcoming.length ? d.upcoming.map(l => (
                <div key={l.id} className="rd-row"><div className="main"><span className="rd-time">{l.appt_date === d.today ? 'Today' : niceDate(l.appt_date)} {time12(l.appt_time)}</span><span className="title">{l.customer}{l.customer_2 ? ` & ${l.customer_2}` : ''}</span>
                  <span className="rd-sub">{l.address}{l.city ? `, ${l.city}` : ''}{l.products.length ? ` · ${l.products.join(', ')}` : ''}{l.meet_with ? ` · meet ${l.meet_with}` : ''}{l.mine === 'set' ? ' · you set this one' : ''}</span>
                  {l.notes && <span className="rd-sub">{l.notes}</span>}</div>
                  <div className="acts"><a className="rd-btn small gold" href={mapLink(l)} target="_blank" rel="noreferrer">Directions</a>{l.phone && <a className="rd-btn small" href={tel(l.phone)}>Call</a>}</div></div>))
                : <div className="rd-empty">No appointments booked for you in the next 14 days.</div>}
            </section>
          </div><div className="rd-stack">
            <section className="rd-sec"><h2 style={{ marginBottom: 10 }}>My jobs right now</h2>
              <div className="rd-rank"><b>{d.pipeline.open}</b><span>{d.pipeline.open === 1 ? 'job' : 'jobs'} sold and still in progress</span></div>
              <p className="rd-note" style={{ margin: '6px 0 10px' }}>Not finished or funded yet. See each one in My Deals.</p>
              <button type="button" className="rd-btn small" onClick={() => setTab('deals')}>Open My Deals</button></section>
            <section className="rd-sec"><h2 style={{ marginBottom: 10 }}>Shortcuts</h2>
              <div className="rd-links">
                <Link className="rd-btn" to="/my-jobs?tab=commission">My commission</Link>
                <Link className="rd-btn" to="/my-jobs">My jobs</Link>
                <Link className="rd-btn" to="/objections">Objection Room</Link>
                <Link className="rd-btn" to="/training">CPR University</Link>
                <Link className="rd-btn" to="/presentations">Presentations</Link>
                <Link className="rd-btn" to="/painting-calculator">Commission Calculator</Link>
                <Link className="rd-btn" to="/investment-review">Investment Review</Link>
              </div>
              <p className="rd-note" style={{ margin: '10px 0 0' }}>Only your own numbers are on this page. Nobody else's sales or pay are shown.</p></section>
          </div></div>
        </>)}

        {tab === 'numbers' && (
          <div className="rd-grid"><div className="rd-stack">
            <section className="rd-sec"><div className="rd-sechead"><h2>Scorecard · {cur.label}</h2><span className="rd-note">{A ? `${A.set} appointments · ${A.sat} sit-downs · ${A.sold} sold` : 'no appointments in this period'}</span></div>
              {A ? <div style={{ overflowX: 'auto' }}><table><thead><tr><th>{cur.label}</th><th>You</th><th>Team avg</th></tr></thead><tbody>
                {rows.map(([label, hint, mine, team, fmt, high]) => (
                  <tr key={label}><td><strong>{label}</strong><span className="rd-sub">{hint}</span>{tag(mine, team, high) && <span style={{ display: 'block', marginTop: 4 }}>{tag(mine, team, high)}</span>}</td>
                    <td className="val">{fmt(mine)}</td><td>{fmt(team)}</td></tr>))}
              </tbody></table></div> : <div className="rd-empty">No appointments under your name in this period. Pick a longer period at the top.</div>}
              {K === 'rep' && A && A.wasted > 0 && <p className="rd-note" style={{ marginTop: 10 }}>{A.wasted} of your trips had no one to sit down with (no show, no opp or NRA). That's on the setting side, not on you, so it isn't counted against your sit-down rate.</p>}
            </section>
            {K === 'rep' && LA && LA.sat > 0 && L.sales.avgTicket ? <WhatIf A={LA} s={L.sales} /> : null}
          </div><div className="rd-stack">
            <section className="rd-sec"><h2>My sales, last 12 months</h2>
              <div className="rd-bars">{d.trend.map(t => (
                <div key={t.month} className={t.month === d.today.slice(0, 7) ? 'cur' : ''} title={`${monthName(t.month)}: ${usd(t.revenue)} · ${t.jobs} jobs`}>
                  <em>{t.revenue ? (t.revenue >= 1000 ? '$' + Math.round(t.revenue / 1000) + 'k' : usd(t.revenue)) : ''}</em><i style={{ height: `${t.revenue / maxTrend * 100}%` }} /><small>{monthName(t.month).slice(0, 1)}</small></div>))}</div>
              <p className="rd-note">Your share of each sale, by the date it was sold. Cancelled jobs are left out.</p>
            </section>
          </div></div>
        )}

        {tab === 'deals' && (
          <section className="rd-sec"><div className="rd-sechead"><h2>My deals</h2><span className="rd-note">{d.deals.length} jobs you're on · {d.pipeline.open} still in progress</span></div>
            {d.deals.length ? d.deals.map(x => { const [tone, label] = stageOf(x); return (
              <div key={x.id} className="rd-row"><div className="main"><span className="title">{x.customer || '—'}</span> <span className={'rd-pill ' + tone}>{label}</span>
                <span className="rd-sub">{x.service ? `${x.service} · ` : ''}sold {niceDate(x.sold)} {x.sold.slice(0, 4)} · PO {x.po}{x.city ? ` · ${x.city}` : ''} · as {x.role.toLowerCase()}</span>
                {x.prod_status && <span className="rd-sub">Production: {x.prod_status}</span>}</div>
                <div style={{ textAlign: 'right' }}><b style={{ font: '700 1.3rem var(--display)' }}>{usd(x.credit)}</b><span className="rd-sub">{x.share < 100 ? `${x.share}% of ${usd(x.value)}` : 'full job'}</span></div></div>); })
              : <div className="rd-empty">No jobs under your name yet.</div>}
          </section>
        )}
      </main>

      {goalOpen && <GoalSheet goal={d.goal} avg={avgForGoal} onClose={() => setGoalOpen(false)} onSaved={g => { setD(x => ({ ...x, goal: g })); setGoalOpen(false); }} />}
    </div>
  );
}

function WhatIf({ A, s }) {
  const [pts, setPts] = useState(5);
  const now = A.sold / A.sat, to = Math.min(now + pts / 100, 1);
  const share = s.full ? s.revenue / s.full : 1;
  const gain = A.sat * (to - now) * s.avgTicket * share;
  return (
    <section className="rd-sec rd-whatif"><h2>What a better sit-down rate is worth</h2>
      <label htmlFor="rd-wi" className="rd-note">Sell {Math.round(to * 100)}% of your sit-downs instead of {Math.round(now * 100)}%</label>
      <input id="rd-wi" type="range" min="1" max="25" value={pts} onChange={e => setPts(+e.target.value)} />
      <output>+{usd(gain)} in sales a month</output>
      <span className="rd-note">Based on your {A.sat} sit-downs this month and your {usd(s.avgTicket)} average ticket.</span>
    </section>
  );
}

function GoalSheet({ goal, avg, onClose, onSaved }) {
  const [v, setV] = useState(goal || 100000);
  const [busy, setBusy] = useState(false);
  useEffect(() => { const k = e => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); }, [onClose]);
  async function save() {
    setBusy(true);
    try { const r = await saveMyGoal(v); toast.success('Goal saved'); onSaved(r.goal); } catch (e) { toast.error('Could not save your goal'); } finally { setBusy(false); }
  }
  return (
    <div className="rd-sheet-bg" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="rd-sheet" role="dialog" aria-label="Monthly sales goal">
        <h2>My monthly sales goal</h2>
        <p className="rd-note">Your share of the sales you're on, per month. Only you see it.</p>
        <output>{usd(v)}</output>
        <input type="range" min="10000" max={Math.max(1000000, v)} step="5000" value={v} onChange={e => setV(+e.target.value)} aria-label="Goal" />
        {avg ? <p className="rd-note">That's about {Math.ceil(v / avg)} sales at your {usd(avg)} average ticket.</p> : null}
        <div className="acts"><button type="button" className="rd-btn" onClick={onClose}>Cancel</button><button type="button" className="rd-btn gold" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save goal'}</button></div>
      </div>
    </div>
  );
}
